export type UserStatus = "ACTIVE" | "BLOCKED" | "DELETED";

export type BlockReason =
  | "MINOR_REQUIRES_CONSENT"
  | "FRAUD_CARD_FINGERPRINT_REUSE"
  | "FRAUD_PHONE_COUNTRY_BLOCKED"
  | "OTHER";

export interface BlockReasonOption {
  value: BlockReason;
  label: string;
  defaultMessage: string;
}

export const BLOCK_REASONS: BlockReasonOption[] = [
  {
    value: "MINOR_REQUIRES_CONSENT",
    label: "Minor - Parental Consent Required",
    defaultMessage:
      "Parental consent is required to use this service. Please contact support for more information.",
  },
  {
    value: "OTHER",
    label: "Other",
    defaultMessage: "",
  },
  {
    value: "FRAUD_CARD_FINGERPRINT_REUSE",
    label: "Fraud - Reused blocked card",
    defaultMessage: "Rental cannot be started for this account.",
  },
  {
    value: "FRAUD_PHONE_COUNTRY_BLOCKED",
    label: "Fraud - Blocked phone country",
    defaultMessage: "Rental cannot be started for this account.",
  },
];

export interface UserBlockInfo {
  isBlocked: boolean;
  blockReason?: BlockReason;
  blockMessage?: string;
  blockInternalNotes?: string;
  blockedAt?: string;
  blockedBy?: string;
}

export interface BlockAuditEntry {
  id: string;
  action: "BLOCK" | "UNBLOCK" | "UPDATE_MESSAGE" | "UPDATE_NOTES";
  previousStatus: UserStatus;
  newStatus: UserStatus;
  blockReason?: BlockReason;
  blockMessage?: string;
  internalNotes?: string;
  resolutionNotes?: string;
  performedBy: string;
  performedAt: string;
}

export interface BlockUserRequest {
  reason: BlockReason;
  message: string;
  internalNotes?: string;
}

export interface UnblockUserRequest {
  resolutionNotes?: string;
}

export interface BlockStatusResponse {
  userId: string;
  status: UserStatus;
  blockReason?: BlockReason;
  blockMessage?: string;
  blockInternalNotes?: string;
  blockedAt?: string;
  blockedBy?: string;
}

export interface BlockHistoryResponse {
  entries: BlockAuditEntry[];
}

export const getStatusColor = (
  status: UserStatus
): "success" | "error" | "default" => {
  switch (status) {
    case "ACTIVE":
      return "success";
    case "BLOCKED":
      return "error";
    case "DELETED":
      return "default";
  }
};

export const getStatusLabel = (status: UserStatus): string => {
  switch (status) {
    case "ACTIVE":
      return "Active";
    case "BLOCKED":
      return "Blocked";
    case "DELETED":
      return "Deleted";
  }
};

export const getBlockReasonLabel = (reason?: BlockReason): string => {
  if (!reason) return "-";
  return BLOCK_REASONS.find((r) => r.value === reason)?.label || reason;
};

export const isFraudBlockReason = (reason?: BlockReason): boolean =>
  Boolean(reason?.startsWith("FRAUD_"));
